import React, {useCallback, useEffect, useState} from 'react';
import {Button, Collapse, Stack, TextField, Typography} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import StopCircle from '@mui/icons-material/StopCircle';
import PolicySelector from './PolicySelector';

/**
 * Collapsible generation form that auto-hides while generating or when output exists.
 */
const GenerationFormCollapsible = ({
                                       instructions,
                                       selectedPolicy,
                                       isGenerating,
                                       isReasoning,
                                       generatedMarkdown,
                                       generatingError,
                                       onInstructionsChange,
                                       onPolicyChange,
                                       onGenerate,
                                       onAbort,
                                       resetKey,
                                       renderToggle,
                                       expanded,
                                       onExpandedChange,
                                       generateDisabled = false,
                                   }) => {
    const isControlled = typeof expanded === 'boolean' && typeof onExpandedChange === 'function';
    const [showForm, setShowForm] = useState(true);
    const [userToggled, setUserToggled] = useState(false);

    const effectiveExpanded = isControlled ? expanded : showForm;

    // Reset when modal context changes
    useEffect(() => {
        if (!isControlled) {
            setShowForm(true);
            setUserToggled(false);
        }
    }, [resetKey, isControlled]);

    // Auto-hide when generating or after output/error; reopen when idle unless user toggled
    useEffect(() => {
        if (isControlled) return;
        const shouldAutoHide = isGenerating || !!generatedMarkdown || !!generatingError;
        if (shouldAutoHide) {
            setShowForm(false);
            return;
        }
        if (!userToggled) {
            setShowForm(true);
        }
    }, [isControlled, isGenerating, generatedMarkdown, generatingError, userToggled]);

    const handleToggle = useCallback(() => {
        if (isControlled) {
            onExpandedChange(!expanded);
            return;
        }
        setUserToggled(true);
        setShowForm(prev => !prev);
    }, [isControlled, onExpandedChange, expanded]);

    return (
        <Stack spacing={1}>
            {typeof renderToggle === 'function'
                ? renderToggle({
                    isExpanded: effectiveExpanded,
                    onToggle: handleToggle,
                    disabled: false,
                })
                : null}

            <Collapse in={effectiveExpanded} unmountOnExit>
                <Stack p={1} px={2} spacing={2}
                       sx={{boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)"}}
                >

                    <PolicySelector
                        selectedPolicy={selectedPolicy}
                        onPolicyChange={onPolicyChange}
                    />

                    <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={700}>
                            Additional Instructions
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            value={instructions}
                            onChange={onInstructionsChange}
                            placeholder="Provide any specific guidance for generating the disclosure...."
                            variant="outlined"
                            size="small"
                        />
                    </Stack>

                    <Stack direction="row" spacing={1} pb={2}>
                        <Button
                            variant="contained"
                            startIcon={<AutoAwesome/>}
                            onClick={onGenerate}
                            disabled={generateDisabled || isGenerating || isReasoning}
                        >
                            Generate
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<StopCircle/>}
                            onClick={onAbort}
                            disabled={!isGenerating && !isReasoning}
                        >
                            Abort
                        </Button>
                    </Stack>
                </Stack>
            </Collapse>
        </Stack>
    );
};

export default GenerationFormCollapsible;

